import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { LocationDictionary } from 'src/lib/fliko/models/dictionary.data';
import { EstateData, EstateDbSettingsData } from 'src/lib/fliko/models/estate/estate.data';
import { EstateDetailsData } from 'src/lib/fliko/models/estate/estate-detail.data';
import { EstateDocEntry } from 'src/lib/fliko/models/estate/estate-doc.data';
import { EstateFeatures } from 'src/lib/fliko/models/estate/estate-features';
import { EstateInsuranceData } from 'src/lib/fliko/models/estate/insurance.data';
import { EstateQrCodeData } from 'src/lib/fliko/models/estate/estate-qr-code.data';
import { EstateStatistics } from 'src/lib/fliko/models/estate/estate-statistics.data';
import { EstateDocAdd, EstateDocUpdate } from 'src/lib/fliko/models/http/estate-doc-update.data';
import { EstateAdminData } from 'src/lib/fliko/models/users/user.data';
import { UpdateIdOnly } from 'src/lib/fliko/types/update.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { FunctionsApiService } from 'src/app/shared/services/functions-api.service';
import { ContextService } from 'src/app/view/user-context/services/context.service';

@Injectable({ providedIn: 'root' })
export class EstateService {
  constructor(
    private dbSrv: DatabaseService,
    private fas: FunctionsApiService,
    private ctxSrv: ContextService
  ) {}

  // #region Estate
  /**
   * @path /estate/$id/
   * @database common
   */
  private estate = (estateId: string) => this.dbSrv.commonFactory.getObject<EstateData>(`estate/${estateId}`);

  public updateEstate(data: UpdateIdOnly<EstateData>): Promise<void> {
    return this.estate(data.id).update(data);
  }

  // #endregion Estate

  // #region Estate database settings
  /**
   * @path /estateDbSettings/$estateId
   * @database common
   */
  private estateDbSettings = (estateId: string) =>
    this.dbSrv.commonFactory.getObject<EstateDbSettingsData>(`estateDbSettings/${estateId}`);

  public getEstateDbSettings$(estateId?: string): Observable<EstateDbSettingsData | Nullish> {
    if (estateId) return this.estateDbSettings(estateId).get();
    return this.ctxSrv
      .getEstateId$()
      .pipe(switchMap((currentEstateId) => (currentEstateId ? this.estateDbSettings(currentEstateId).get() : of(null))));
  }

  public getEstateDbSettingsFirst(estateId: string): Promise<EstateDbSettingsData | Nullish> {
    return this.estateDbSettings(estateId).first();
  }

  public updateEstateDbSettings(data: EstateDbSettingsData): Promise<void> {
    return this.estateDbSettings(data.id).update(data);
  }
  // #endregion Estate database settings

  // #region Estate Details
  /** @path /estateDetails/$estateId/ */
  private get estateDetails() {
    return this.dbSrv.estateFactory.getObject<EstateDetailsData>('estateDetails/$eId');
  }

  public getDetails$(): Observable<EstateDetailsData | Nullish> {
    return this.estateDetails.get();
  }

  public getDetailsOnce(cancel$?: Observable<unknown>): Promise<EstateDetailsData | Nullish> {
    return this.estateDetails.snapshot(cancel$);
  }

  public updateDetails(data: UpdateIdOnly<EstateDetailsData>): Promise<void> {
    return this.estateDetails.update(data);
  }

  public updateDeveloper(developerId: string): Promise<void> {
    return this.estateDetails.update({ developerId: developerId });
  }
  // #endregion Details

  // #region QR Codes
  /** @path /estateQrCodes/$estateId/ */
  private get estateQrCodes() {
    return this.dbSrv.estateFactory.getCollection<EstateQrCodeData>('estateQrCodes/$eId');
  }

  public getQrCodes$(): Observable<EstateQrCodeData[]> {
    return this.estateQrCodes.getList();
  }
  // #endregion QR Codes

  // #region Admin
  /**
   * @path /estateAdmin/
   * @key 'userId | estateId'
   */
  private estateAdmin = (estateId: string) =>
    this.dbSrv.estateFactory.getCollectionReadOnly<EstateAdminData>('estateAdmin', {
      by: { child: 'estateId' },
      equalTo: estateId
    });

  public getAdmins$(): Observable<EstateAdminData[]> {
    return this.ctxSrv.getEstateId$().pipe(switchMap((id) => (id ? this.estateAdmin(id).getList() : of([]))));
  }

  public getAdminsMap$(): Observable<Map<string, EstateAdminData>> {
    return this.ctxSrv.getEstateId$().pipe(switchMap((id) => (id ? this.estateAdmin(id).getMap() : of(new Map()))));
  }

  // #endregion Admin

  // #region Insurance
  /**
   * @path /estateInsurance/$estateId/
   * @key id
   */
  private get estateInsurance() {
    return this.dbSrv.estateFactory.getCollection<EstateInsuranceData>('estateInsurance/$eId');
  }

  private estateInsuranceByEndDate = (from: number, to: number) =>
    this.dbSrv.estateFactory.getCollectionReadOnly<EstateInsuranceData>('estateInsurance/$eId', {
      by: { child: 'endDate' },
      startAt: from,
      endAt: to
    });

  public getInsurances$(): Observable<EstateInsuranceData[]> {
    return this.estateInsurance.getList();
  }

  /**
   * @param {number} from (optional) endDate filter values
   * @param {number} to   (optional) endDate filter values
   * @return
   */
  public getInsurancesOnce(from?: number, to?: number): Promise<EstateInsuranceData[]> {
    if (from && to) return this.estateInsuranceByEndDate(from, to).onceList();
    else return this.estateInsurance.onceList();
  }

  public getInsurance$(id: string): Observable<EstateInsuranceData | Nullish> {
    return this.estateInsurance.getElement(id);
  }

  public setInsurance(data: EstateInsuranceData): Promise<void> {
    return this.estateInsurance.setElement(data.id, data);
  }

  public updateInsurance(data: UpdateIdOnly<EstateInsuranceData>): Promise<void> {
    return this.estateInsurance.updateElement(data.id, data);
  }
  // #endregion

  // #region Files
  /**
   * @path /estateDocuments/$estateId
   * @key id
   */
  private get estateDocumentsRoot() {
    return this.dbSrv.estateFactory.getCollection<EstateDocEntry>('estateDocs/$eId');
  }

  public getDocs$(): Observable<Record<string, EstateDocEntry>> {
    return this.estateDocumentsRoot.getObject().pipe(map((x) => x ?? {}));
  }

  public setFile(req: EstateDocAdd): Promise<boolean> {
    return this.fas.callFunction('addEstateDoc', req, (e) => {
      throw e;
    });
  }

  public async updateFile(req: EstateDocUpdate): Promise<boolean> {
    return this.fas.callFunction('updateEstateDoc', req, (e) => {
      throw e;
    });
  }

  // #endregion Files

  // #region Statistics
  /** @path /estateStatistics/$estateId/ */
  private get estateStatistics() {
    return this.dbSrv.estateFactory.getObject<EstateStatistics>('estateStatistics/$eId');
  }

  public getStatistics$(): Observable<EstateStatistics | Nullish> {
    return this.estateStatistics.get();
  }
  // #endregion Statistics

  // #region Location Dictionary
  /** @path /dictionary/location/$estateId/ */
  private get locationDictionary() {
    return this.dbSrv.estateFactory.getObject<LocationDictionary>('dictionary/location/$eId');
  }

  public getLocationDictionary$(): Observable<LocationDictionary | Nullish> {
    return this.locationDictionary.get();
  }
  // #endregion Location Dictionary

  // #region Features
  /** @path /estateFeatures/$estateId/ */
  private get estateFeatures() {
    return this.dbSrv.estateFactory.getObject<EstateFeatures>('estateFeatures/$eId');
  }

  public getFeatures$(): Observable<EstateFeatures | Nullish> {
    return this.estateFeatures.get();
  }

  public getFeaturesOnce(cancel$?: Observable<unknown>): Promise<EstateFeatures | Nullish> {
    return this.estateFeatures.snapshot(cancel$);
  }

  public getFeaturesFirst(): Promise<EstateFeatures | Nullish> {
    return this.estateFeatures.first();
  }
  // #endregion Features
}
